import { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import PropTypes from 'prop-types';
import {
  XMarkIcon,
  FireIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  LightBulbIcon,
  PaperAirplaneIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  clearGconChat,
  generateGconSessionInsights,
  getGconChat,
  getGconSessionInsights,
  sendGconChat,
} from '@/api/gcon';
import { confirm } from '@/components/core/createConfirmation';
import { downloadChatTranscript, insightsKey, starterQuestions } from '@/utils/gconUtils';

const MARKDOWN_COMPONENTS = {
  h1: ({ children }) => <p className="font-semibold text-gray-800 mt-2 mb-0.5">{children}</p>,
  h2: ({ children }) => <p className="font-semibold text-gray-800 mt-2 mb-0.5">{children}</p>,
  h3: ({ children }) => <p className="font-semibold text-gray-800 mt-1">{children}</p>,
  p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-4 mb-1 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-4 mb-1 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li>{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-gray-800">{children}</strong>,
  code: ({ children }) => <code className="bg-gray-100 rounded px-1 text-xs font-mono">{children}</code>,
  // Wide tables scroll inside the bubble rather than stretching the panel
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="w-full text-xs border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-100 text-gray-600">{children}</thead>,
  tbody: ({ children }) => <tbody className="[&>tr:nth-child(even)]:bg-gray-50">{children}</tbody>,
  tr: ({ children }) => <tr className="border-b border-gray-100 last:border-b-0">{children}</tr>,
  th: ({ children }) => (
    <th className="px-2.5 py-1.5 font-semibold whitespace-nowrap text-center first:text-left">{children}</th>
  ),
  // Row labels sit in the first column and every value column is centred. This
  // goes by position, not content, so bolded numbers align too, and it ignores
  // any column alignment the model writes into its markdown.
  td: ({ children }) => (
    <td className="px-2.5 py-1.5 text-gray-700 tabular-nums text-center first:text-left">{children}</td>
  ),
};

const InsightSection = ({ heading, body, icon }) => {
  const Icon = icon === 'fire' ? FireIcon : icon === 'trending' ? ArrowTrendingUpIcon : LightBulbIcon;
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-teal-500 flex-shrink-0" />
        <h3 className="text-sm font-semibold text-gray-800">{heading}</h3>
      </div>
      <div className="text-sm text-gray-600 leading-relaxed pl-6">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
          {body ?? ''}
        </ReactMarkdown>
      </div>
    </div>
  );
};

const InsightsPanel = ({ session, result, contextLabel, onClose }) => {
  const [generating, setGenerating] = useState(true);
  const [insights, setInsights] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [genLabel, setGenLabel] = useState(contextLabel);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  // Always read the live analysis without making it an effect dependency —
  // regenerating on every data change would wipe an in-progress conversation.
  const liveRef = useRef({ result, contextLabel });
  liveRef.current = { result, contextLabel };

  const runIdRef = useRef(0);
  const begin = () => {
    const runId = ++runIdRef.current;
    const { result: data, contextLabel: label } = liveRef.current;
    setGenerating(true);
    setGenLabel(label);
    return { runId, data };
  };
  const finish = (runId, entry) => {
    if (runId !== runIdRef.current) return;
    setInsights(entry?.sections ?? null);
    setGeneratedAt(entry?.generated_at ?? null);
    setGenerating(false);
  };
  const generate = () => {
    const { runId, data } = begin();
    generateGconSessionInsights(session.id, insightsKey(data.selection), data)
      .then(r => finish(runId, r.data))
      .catch(() => finish(runId, null));
  };
  // Insights are saved per month selection, so reopening the panel shows the
  // last result instantly; only a selection without one goes to the model.
  const load = () => {
    const { runId, data } = begin();
    getGconSessionInsights(session.id, insightsKey(data.selection))
      .then(r => finish(runId, r.data))
      .catch(err => {
        if (runId !== runIdRef.current) return;
        if (err?.response?.status === 404) generate();
        else finish(runId, null);
      });
  };

  // Load once when the panel opens; afterwards it's user-driven.
  const ranRef = useRef(false);
  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    load();
  });

  // The chat is saved on the session, so reopening the panel picks it back up
  useEffect(() => {
    getGconChat(session.id)
      .then(r => setChatMessages(r.data))
      .catch(() => {});
  }, [session.id]);

  const savedMessages = chatMessages.filter(m => !m.unsaved);

  const clearChat = async () => {
    if (await confirm('Clear this chat? The saved conversation will be deleted.')) {
      await clearGconChat(session.id);
      setChatMessages([]);
    }
  };

  useEffect(() => {
    if (!expanded) return;
    const onKey = e => e.key === 'Escape' && setExpanded(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  // Expanded, the panel floats over the analysis; keep text to a readable width
  const column = expanded ? 'max-w-3xl mx-auto w-full' : '';

  const stale = genLabel !== contextLabel;

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [chatMessages, chatLoading]);

  const sendMessage = async question => {
    const text = (question ?? chatInput).trim();
    if (!text || chatLoading) return;
    // Shown straight away; replaced by the saved turn once the server answers
    const pending = { role: 'user', content: text, unsaved: true };
    const before = chatMessages;
    setChatMessages([...before, pending]);
    setChatInput('');
    setChatLoading(true);
    try {
      const r = await sendGconChat(session.id, text, result);
      setChatMessages([...before, ...r.data]);
    } catch (err) {
      // Failed turns are never saved server-side, so mark them as local-only
      const detail = err?.response?.data?.detail ?? 'Sorry, could not get a response.';
      setChatMessages([...before, pending, { role: 'assistant', content: `${detail} (not saved)`, unsaved: true }]);
    } finally {
      setChatLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKey = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {expanded && (
        <div className="fixed inset-0 z-40 bg-gray-900/30" onClick={() => setExpanded(false)} aria-hidden="true" />
      )}
      {/* Docked by default so the rest of the UI stays usable across tab and
          filter changes; expanding floats the same panel (and chat) on top. */}
      <div
        role={expanded ? 'dialog' : undefined}
        aria-modal={expanded || undefined}
        aria-label="AI Insights"
        className={
          expanded
            ? 'fixed z-50 inset-4 md:inset-x-[8%] md:inset-y-8 bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col min-h-0'
            : 'w-[34rem] max-w-[45vw] flex-shrink-0 h-full bg-white border-l border-gray-200 flex flex-col min-h-0'
        }
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <SparklesIcon className="w-5 h-5 text-teal-500 flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-800">AI Insights</h2>
              <p className="text-xs text-gray-400 truncate">{genLabel}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => downloadChatTranscript(session.name, savedMessages)}
              disabled={!savedMessages.length}
              title="Download chat transcript (.txt)"
              className="p-1.5 rounded hover:bg-gray-100 text-gray-400 disabled:opacity-40"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={clearChat}
              disabled={!savedMessages.length || chatLoading}
              title="Clear chat"
              className="p-1.5 rounded hover:bg-gray-100 text-gray-400 disabled:opacity-40"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={generate}
              disabled={generating}
              title="Regenerate for the current selection"
              className="p-1.5 rounded hover:bg-gray-100 text-gray-400 disabled:opacity-40"
            >
              <ArrowPathIcon className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
            </button>
            <button
              type="button"
              onClick={() => setExpanded(v => !v)}
              title={expanded ? 'Collapse to side panel' : 'Expand'}
              aria-label={expanded ? 'Collapse to side panel' : 'Expand'}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-400"
            >
              {expanded ? <ArrowsPointingInIcon className="w-4 h-4" /> : <ArrowsPointingOutIcon className="w-4 h-4" />}
            </button>
            <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 text-gray-400">
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Stale-data notice */}
        {stale && !generating && (
          <div className="px-5 py-2 bg-amber-50 border-b border-amber-100 flex items-center justify-between gap-2 flex-shrink-0">
            <p className="text-xs text-amber-700 truncate">Selection changed to {contextLabel}</p>
            <button
              type="button"
              onClick={generate}
              className="text-xs font-medium text-amber-700 hover:text-amber-900 underline flex-shrink-0"
            >
              Regenerate
            </button>
          </div>
        )}

        {/* Body */}
        <div ref={bodyRef} className="flex-1 overflow-y-auto px-5 py-4">
          <div className={column}>
            {generating ? (
              <div className="flex flex-col items-center justify-center h-40 gap-3">
                <div className="flex gap-1">
                  {[0, 1, 2].map(i => (
                    <div
                      key={i}
                      className="w-2 h-2 rounded-full bg-teal-400 animate-bounce"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    />
                  ))}
                </div>
                <p className="text-sm text-gray-400">Analysing hot spot patterns…</p>
              </div>
            ) : insights ? (
              <>
                {generatedAt && (
                  <p className="text-xs text-gray-400 mb-3">Generated {new Date(generatedAt).toLocaleString()}</p>
                )}
                {insights.map(s => (
                  <InsightSection key={s.heading} {...s} />
                ))}
              </>
            ) : (
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 flex items-start gap-2">
                <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Insights could not be generated.{' '}
                  <button type="button" onClick={generate} className="underline font-medium">
                    Try again
                  </button>
                </p>
              </div>
            )}
            {!generating && chatMessages.length === 0 && !chatLoading && (
              <div className="mt-4 border-t border-gray-100 pt-4">
                <p className="text-xs font-medium text-gray-500 mb-2">Try asking</p>
                <div className="flex flex-col items-start gap-1.5">
                  {starterQuestions(result).map(q => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => sendMessage(q)}
                      className="text-left text-xs px-3 py-1.5 rounded-full border border-teal-200 bg-teal-50 text-teal-800 hover:bg-teal-100"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {chatMessages.length > 0 && (
              <div className="mt-4 border-t border-gray-100 pt-4 space-y-3">
                {chatMessages.map((m, i) => (
                  <div
                    key={m.id ?? `pending-${i}`}
                    className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`rounded-xl px-3 py-2 text-sm leading-relaxed ${
                        m.role === 'user'
                          ? 'max-w-[85%] bg-teal-500 text-white'
                          : 'w-full min-w-0 bg-gray-50 text-gray-700'
                      }`}
                    >
                      {m.role === 'assistant' ? (
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
                          {m.content}
                        </ReactMarkdown>
                      ) : (
                        m.content
                      )}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-50 rounded-xl px-3 py-2 flex gap-1 items-center">
                      {[0, 1, 2].map(i => (
                        <div
                          key={i}
                          className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-bounce"
                          style={{ animationDelay: `${i * 0.15}s` }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Chat input */}
        <div className="px-4 py-3 border-t border-gray-100">
          <div className={column}>
            <div className="flex items-end gap-2 bg-gray-50 rounded-xl px-3 py-2">
              <textarea
                ref={inputRef}
                rows={1}
                className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 resize-none outline-none"
                placeholder="Ask a follow-up question…"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={handleKey}
                style={{ maxHeight: '96px', overflowY: 'auto' }}
              />
              <button
                type="button"
                onClick={() => sendMessage()}
                disabled={!chatInput.trim() || chatLoading}
                className="p-1 rounded-lg text-teal-500 disabled:opacity-30 hover:bg-teal-50 transition-colors"
              >
                <PaperAirplaneIcon className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">Powered by Sesame Analyst · Prototype</p>
          </div>
        </div>
      </div>
    </>
  );
};

InsightSection.propTypes = {
  heading: PropTypes.string.isRequired,
  body: PropTypes.string,
  icon: PropTypes.string,
};

InsightsPanel.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  result: PropTypes.object.isRequired,
  contextLabel: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default InsightsPanel;
